<template>
  <div
    v-if="isLoading"
    class="quick-estimate"
  >
    <div class="v-room-enquiry__wrap">
      <div class="quick-estimate__wrapper">
        <div class="quick-estimate__placeholder">
          <div class="loading-overlay-base load-results">
            <div class="spinner" />
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    v-else
    data-test-id="enquiry-form-container"
  >
    <teleport
      :to="attachFormTarget"
    >
      <div
        ref="enquiryForm"
        data-test-id="enquiry-form__wrapper"
      >
        <PageTile
          border-type="shadow"
          class="mt-4 mb-4"
        >
          <template v-if="isBigDesktop">
            <EnquiryFormHeader
              v-if="!$store.getters.isRtbFlowPossible
                || (($store.state.step === $store.state.STEPS.messageAndCatering
                  || $store.state.step === $store.state.STEPS.userInfo) && !$store.state.requestToBookPath)"
              class="mb-4 justify-center md:justify-start"
            />
            <EnquiryPricingBar
              v-if="$store.getters.isRtbFlowPossible
                && !$store.state.user.lastEnquiry.date
                && !$store.state.showSuspensionWarning
                && !$store.state.leadHubOffer
                && (($store.state.step !== $store.state.STEPS.messageAndCatering
                  && $store.state.step !== $store.state.STEPS.userInfo))"
            />

            <div v-if="$store.state.showSuspensionWarning && !$store.state.user.lastEnquiry.date">
              <SuspensionWarning :class="{'mt-6': !$store.getters.isRtbFlowPossible}" />
              <EnquiryFormBottom class="mt-8" />
            </div>

            <div v-else-if="$store.state.user.lastEnquiry.date">
              <AlreadyEnquired />
              <EnquiryFormBottom :class="{'mt-8': !$store.getters.isRtbFlowPossible, 'mt-4': $store.getters.isRtbFlowPossible}" />
            </div>

            <div v-else-if="$store.state.leadHubOffer">
              <HasLeadHubOffer
                class="my-6"
              />
              <EnquiryFormBottom class="mt-8" />
            </div>

            <div v-else-if="$store.getters.isRtbFlowPossible">
              <EnquiryForm @openModal="(data) => openModal(data)" />
            </div>

            <div v-else>
              <EnquiryForm />
              <EnquiryFormBottom class="mt-8" />
            </div>
          </template>
          <template v-else>
            <EnquiryFormHeader
              v-if="!$store.getters.isRtbFlowPossible"
              class="justify-center md:justify-start mb-4"
            />
            <EnquiryPricingBar v-if="$store.getters.isRtbFlowPossible" />
            <MobileStartEnquiryBtn
              @click="openModal({ requestToBook: true, requestToBookMobilePath: true })"
            />
            <div
              v-if="$store.getters.isRtbFlowPossible"
              class="text-grey-600 text-xs flex items-center justify-center mt-4"
            >
              You won't be charged yet
            </div>
          </template>
        </PageTile>

        <PageTile
          v-if="$store.getters.isRtbFlowPossible
            && !$store.state.user.lastEnquiry?.date
            && !$store.state.leadHubOffer
            && !$store.state.showSuspensionWarning"
          border-type="shadow"
          class="mt-4 mb-4"
        >
          <EnquiryFormHeader
            class="mb-4 justify-center md:justify-start"
          />
          <BaseButton
            color="white"
            class="w-full flex items-center justify-center"
            data-test-id="message-host-btn"
            @click="messageHostBtnClicked"
          >
            Message host
          </BaseButton>
        </PageTile>

        <AddToFavourites v-if="!$store.state.isVenueSuspended" />

        <EnquiryFormModal
          v-if="$store.state.isEnquiryFormModalOpen"
          @close="$store.commit('setIsEnquiryFormModalOpen', false)"
        />
      </div>

      <MobileBottomButton
        v-if="!$store.state.user.lastEnquiry.date"
        :is-visible="showMobileButtons"
        @click="openModal({ requestToBook: true, requestToBookMobilePath: true })"
      />
    </teleport>

    <teleport to="#enquiry-sent-modal">
      <EnquirySentModal :config="$store.state.enquirySentModalConfig" />

      <ReviewModal
        :venue-name="$store.state.venue.name"
        :venue-id="$store.state.venue.id"
        @track="onTrackReviewsModal"
      />
      <MapModal :map-config="$store.state.map" :title="`${$store.state.room.name} at ${$store.state.venue.name}`" />
    </teleport>

    <WishlistModals />
  </div>
</template>

<script>
import ScreenWidth from '@/components/ScreenWidth.js';
import PageTile from '@/components/TailwindComponents/PageTile.vue';
import BaseButton from '@/components/Ui/Buttons/BaseButton.vue';
import { setContextParam } from '@/services/context-store.js';
import ElementVisibility from '@/services/elementVisibility.js';

import AddToFavourites from './components/AddToFavourites.vue';
import AlreadyEnquired from './components/AlreadyEnquired.vue';
import EnquiryForm from './components/EnquiryForm/EnquiryForm.vue';
import EnquiryFormBottom from './components/EnquiryFormBottom.vue';
import EnquiryFormHeader from './components/EnquiryFormHeader/EnquiryFormHeader.vue';
import EnquiryFormModal from './components/EnquiryFormModal.vue';
import EnquiryPricingBar from './components/EnquiryPricingBar.vue';
import EnquirySentModal from './components/EnquirySentModal.vue';
import HasLeadHubOffer from './components/HasLeadHubOffer.vue';
import MapModal from './components/MapModal.vue';
import MobileBottomButton from './components/MobileBottomButton/MobileBottomButton.vue';
import MobileStartEnquiryBtn from './components/MobileStartEnquiryBtn.vue';
import ReviewModal from './components/ReviewModal/ReviewModal.vue';
import SuspensionWarning from './components/SuspensionWarning.vue';
import WishlistModals from './components/WishlistModals.vue';
import initFlyingSidebar from './flyingEnquiryFormSidebar.js';
import { parseFormToBackend } from './misc/parsePayload';

export default {
  name: 'EnquiryFormWrapper',
  mixins: [ScreenWidth],
  components: {
    HasLeadHubOffer,
    WishlistModals,
    EnquirySentModal,
    BaseButton,
    EnquiryFormBottom,
    EnquiryPricingBar,
    SuspensionWarning,
    MobileStartEnquiryBtn,
    AlreadyEnquired,
    MobileBottomButton,
    EnquiryFormModal,
    EnquiryForm,
    EnquiryFormHeader,
    AddToFavourites,
    PageTile,
    ReviewModal,
    MapModal,
  },
  data() {
    return {
      isLoading: true,
      isBelowForm: false,
      elementVisibility: null,
      messageHostFirstInteractionTracked: false,
    };
  },

  computed: {
    attachFormTarget() {
      if (this.isBigDesktop) {
        return '#desktop-enquiry-form';
      }
      return '#modal-enquiry-form';
    },
    showMobileButtons() {
      return this.isBelowForm && !this.isBigDesktop;
    },
  },

  watch: {
    '$store.state.isEnquiryFormModalOpen': function () {
      const parentContainer = document.querySelector('#desktop-enquiry-form');
      if (this.$store.state.isEnquiryFormModalOpen) {
        parentContainer.style.position = 'static';
      } else {
        parentContainer.style.position = '';
      }
    },
    isLoading(isLoading) {
      if (isLoading) {
        return;
      }
      App.es6.shared.waitFor(() => {
        return this.$refs.enquiryForm;
      }, () => {
        this.elementVisibility = new ElementVisibility(this.$refs.enquiryForm, ({ isBelow }) => {
          this.isBelowForm = isBelow;
        });
      });

      this.$nextTick(() => {
        this.trackFirstInteraction();
      });
    },

    isBigDesktop: {
      handler(isBigDesktop) {
        this.$store.commit('setBigDesktop', isBigDesktop);
        if (isBigDesktop) {
          this.$store.commit('setIsEnquiryFormModalOpen', false);
        }
      },
      immediate: true,
    },
  },

  methods: {
    openModal({ requestToBook, requestToBookMobilePath = null }) {
      if (
        (requestToBook === true && requestToBookMobilePath === true)
        || (requestToBook === false && requestToBookMobilePath === null && this.$store.getters.isRtbFlowPossible)
      ) {
        setContextParam('UserCabinet', 'room_page');
      }

      this.$store.commit('setIsEnquiryFormModalOpen', true);
      let label = 'modal-open';
      if (this.$store.getters.isRtbFlowPossible) {
        this.$store.commit('setRequestToBookPath', requestToBook);
        this.$store.commit('setRequestToBookMobilePath', requestToBookMobilePath);
        label = requestToBook ? 'open-request-to-book' : 'open-message-host';
      }

      this.$store.dispatch('trackEnquiryFormNavigation', {
        label,
      });
    },

    navigateToEnquiryForm() {
      this.openModal({ requestToBook: false });
    },

    trackFirstInteraction() {
      const handler = (event) => {
        const name = event.target.getAttribute('data-test-id');
        if (name) {
          this.$store.dispatch('trackEnquiryFormFirstInteraction', name);
          this.$refs.enquiryForm.removeEventListener('mousedown', handler);
        }
      };
      // Click is buggy on Chrome on html select fields, so I use mousedown
      this.$refs.enquiryForm.addEventListener('mousedown', handler);
    },
    messageHostBtnClicked() {
      this.$store.dispatch('trackGA4Interaction', {
        section: this.isBigDesktop ? 'event_info' : 'mobile_widget',
        element: 'message_host_button',
        value: null,
        interaction: 'click',
      });

      if (!this.messageHostFirstInteractionTracked) {
        this.$store.dispatch('trackEnquiryFormFirstInteraction', 'message-host-btn');
        this.messageHostFirstInteractionTracked = true;
      }

      this.openModal({ requestToBook: false });
    },
    initTabVisibilityHandler() {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.$store.dispatch('getManagerLastActivityOnline');
          this.$store.dispatch('fetchLastRfpData');
        } else {
          this.saveFormData();
        }
      });
    },
    saveFormData() {
      const formDataKeeper = App.es6.views.quick_estimate.form_data_keeper();
      const state = this.$store.state;

      const payload = parseFormToBackend({
        form: state.form,
        tags: state.eventTypes,
        l10n: state.l10n,
        enquiry_comment: state.form.messageAndCatering.message,
      });

      formDataKeeper.mergeData(payload);
    },
    initUnloadHandler() {
      window.addEventListener('beforeunload', () => {
        this.saveFormData();
      });
    },
    onTrackReviewsModal(data) {
      App.track.event('room_page_interaction', data);
    },
  },

  beforeCreate() {
    this.$store.dispatch('init')
      .then(() => {
        this.isLoading = false;
        this.$store.dispatch('openPricingForDate');
      });
  },

  mounted() {
    this.initUnloadHandler();

    $.subscribe('wishList:added_from_url', () => {
      this.$store.commit('wishlist/setIsRoomFavourite', true);
    });

    $.subscribe('loginState:change', () => {
      this.$store.dispatch('fetchUser');
    });

    window.addEventListener('load', () => {
      window.gtag('get', window.__GA4_MEASURMENT_ID, 'client_id', clientId => {
        window.$store.commit('setAnalyticsClientId', clientId);
      });
    });

    initFlyingSidebar();

    this.$store.dispatch('getManagerLastActivityOnline');
    this.initTabVisibilityHandler();
  },
};
</script>
